// City and country for events: the feed cards and the Locations city list.

// Turns a Mapbox geocoding result into what we store when someone picks a
// suggestion. Mapbox tells us the city ("place") and country separately, so we
// don't have to guess them from the address text.
export function placeFromMapboxFeature(feature) {
  const types = feature.place_type || [];
  const context = feature.context || [];
  const fromContext = (type) => (context.find((item) => String(item.id || '').startsWith(`${type}.`)) || {}).text || '';

  return {
    id: feature.id,
    label: feature.place_name,
    center: feature.center,
    bbox: feature.bbox || null,
    city: types.includes('place') ? feature.text : fromContext('place') || fromContext('locality'),
    country: types.includes('country') ? feature.text : fromContext('country'),
  };
}

const LEADING_POSTCODE = /^\d{4,6}\s+/; // "12047 Berlin"
const TRAILING_POSTCODE = /\s+\d{4,6}(-\d{4})?$/; // "Illinois 62701"

function looksLikePostcode(value) {
  const compact = value.replace(/\s+/g, '');
  return /\d/.test(compact) && compact.length <= 8;
}

// Best guess from plain address text, for typed locations and older events.
// Walks back from the country, skipping postcodes ("N16 7ER") and using the
// part before "State ZIP" in US-style addresses.
export function parseCityCountry(location = '') {
  const parts = String(location).split(',').map((part) => part.trim()).filter(Boolean);

  if (parts.length === 0) return { city: '', country: '' };
  if (parts.length === 1) return { city: parts[0].replace(LEADING_POSTCODE, '').trim(), country: '' };

  const country = parts[parts.length - 1];

  for (let index = parts.length - 2; index >= 0; index -= 1) {
    const part = parts[index];
    if (looksLikePostcode(part)) continue;

    if (TRAILING_POSTCODE.test(part) && index > 0) {
      // "Springfield, Illinois 62701": the city is the part before the state.
      return { city: parts[index - 1].replace(LEADING_POSTCODE, '').trim(), country };
    }

    const city = part.replace(LEADING_POSTCODE, '').trim();
    if (city && !/\d/.test(city)) return { city, country };
  }

  return { city: '', country };
}

// Prefers the city/country saved with the event; falls back to the best guess.
export function getCityCountry(event = {}) {
  if (event.city || event.country) {
    return { city: event.city || '', country: event.country || '' };
  }
  return parseCityCountry(event.location || '');
}
